import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Container,
  Divider,
  Flex,
  Image,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "react-hook-form";
import { PasswordInput, TextInput } from "react-hook-form-mantine";
import { GoogleLogin } from "@react-oauth/google";
import AppleSignin from "react-apple-signin-auth";
import { AppleIcon } from "../common/AppleIcon";
import { web_acceptInviteCreateAccountSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";

import {
  useAcceptWorkspaceInvite,
  useGetWorkspaceInvite,
} from "../../api/workspace";
import Logo from "../../assets/logo-02.svg";
import LoadingPage from "../common/LoadingPage";

const STATUS_MESSAGES = {
  expired: "This invitation has expired. Ask the workspace owner to resend it.",
  revoked: "This invitation has been revoked.",
  accepted: "This invitation has already been accepted. Please log in.",
};

// Google and Apple accepts post the provider credential to the same endpoint
// as the password form. The API verifies the token and rejects it unless the
// identity it vouches for is the invited address.
function SocialAccept({ token, onAccepted }) {
  const { mutate, isError, error } = useAcceptWorkspaceInvite(token);

  return (
    <Flex direction="column" gap="md">
      <GoogleLogin
        shape="pill"
        locale="en"
        logo_alignment="center"
        width="100%"
        onSuccess={(credentialResponse) =>
          mutate({ google: credentialResponse }, { onSuccess: onAccepted })
        }
      />
      <AppleSignin
        authOptions={{
          clientId: "rs.roundcube.congrs.service",
          scope: "email name",
          redirectURI: window.location.origin + "/callback",
          state: "",
          nonce: "nonce",
          usePopup: true,
        }}
        onSuccess={(response) =>
          mutate(
            {
              apple: { id_token: response.authorization.id_token },
              // Apple sends the name only on the first authorization, and
              // never inside the token, so pass it through when present.
              firstName: response.user?.name?.firstName,
              lastName: response.user?.name?.lastName,
            },
            { onSuccess: onAccepted }
          )
        }
        render={({ onClick }) => (
          <Button
            h={40}
            radius="xl"
            color="black"
            variant="filled"
            leftSection={<AppleIcon />}
            onClick={onClick}
          >
            Sign in with Apple
          </Button>
        )}
      />
      {isError && (
        <Text c="red" size="sm">
          {error?.message || "Could not accept invitation."}
        </Text>
      )}
    </Flex>
  );
}

function CreateAccountForm({ token, workspaceName, onAccepted }) {
  const { mutate, isPending, isError, error } = useAcceptWorkspaceInvite(token);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
    resolver: resolver(web_acceptInviteCreateAccountSchema, { $data: true }),
  });

  const onSubmit = ({ firstName, lastName, password }) =>
    mutate({ firstName, lastName, password }, { onSuccess: onAccepted });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Text mb="md">
        Create your Congrs account to join <strong>{workspaceName}</strong>.
      </Text>
      <TextInput
        label="First name"
        name="firstName"
        control={control}
        withAsterisk
      />
      <TextInput
        label="Last name"
        name="lastName"
        control={control}
        withAsterisk
        mt="md"
      />
      <PasswordInput
        label="Password"
        name="password"
        control={control}
        withAsterisk
        mt="md"
      />
      <PasswordInput
        label="Confirm password"
        name="confirmPassword"
        control={control}
        withAsterisk
        mt="md"
      />
      {isError && (
        <Text c="red" mt="md">
          {error?.message || "Could not accept invitation."}
        </Text>
      )}
      <Button type="submit" fullWidth size="md" my={16} loading={isPending}>
        Create account & join
      </Button>
    </form>
  );
}

// The invited address got a Congrs account after the invitation went out. The
// account is added as it would have been at invite time — no credentials, no
// login from the link — and the person signs in as they always do.
function JoinWithAccount({ token, email, workspaceName, onAccepted }) {
  const { mutate, isPending, isError, error } = useAcceptWorkspaceInvite(token);

  return (
    <>
      <Text mb="md">
        You already have a Congrs account for <strong>{email}</strong>. Join{" "}
        <strong>{workspaceName}</strong> with it.
      </Text>
      {isError && (
        <Text c="red" mb="md">
          {error?.message || "Could not accept invitation."}
        </Text>
      )}
      <Button
        fullWidth
        size="md"
        loading={isPending}
        onClick={() => mutate({}, { onSuccess: onAccepted })}
      >
        Join workspace
      </Button>
    </>
  );
}

export default function WorkspaceInviteAccept() {
  const { token } = useParams();
  // Name of the workspace just joined. Kept in state because accepting
  // invalidates the invite query, which then resolves to a bare "accepted"
  // status without the name.
  const [joinedWorkspace, setJoinedWorkspace] = useState(null);
  const { data, isLoading, isError } = useGetWorkspaceInvite(token);

  // A new account comes back with a JWT: persist it and do a full navigation
  // so AuthContext re-bootstraps logged in. An existing account is only added,
  // so the page sends it to the login form instead.
  const onAccepted = (result) => {
    if (result.token) {
      localStorage.setItem("token", result.token);
      window.location.assign("/app");
      return;
    }

    setJoinedWorkspace(data?.workspaceName ?? "");
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  let content;
  if (joinedWorkspace !== null) {
    content = (
      <>
        <Alert color="green" mb="md">
          {joinedWorkspace ? (
            <>
              You're in. Log in to manage <strong>{joinedWorkspace}</strong>.
            </>
          ) : (
            "You're in. Log in to get started."
          )}
        </Alert>
        <Button component={Link} to="/login" fullWidth size="md">
          Go to login
        </Button>
      </>
    );
  } else if (isError || !data) {
    content = <Alert color="red">This invitation link is invalid.</Alert>;
  } else if (data.status !== "pending") {
    content = (
      <Alert color="red">
        {STATUS_MESSAGES[data.status] || "This invitation is no longer valid."}
      </Alert>
    );
  } else if (data.accountExists) {
    content = (
      <JoinWithAccount
        token={token}
        email={data.email}
        workspaceName={data.workspaceName}
        onAccepted={onAccepted}
      />
    );
  } else {
    content = (
      <>
        <CreateAccountForm
          token={token}
          workspaceName={data.workspaceName}
          onAccepted={onAccepted}
        />
        <Divider label="or" labelPosition="center" my="md" />
        <SocialAccept token={token} onAccepted={onAccepted} />
      </>
    );
  }

  return (
    <Container size={420}>
      <Image src={Logo} alt="congrs logo" />
      <Title mb="md">Workspace invitation</Title>
      {content}
    </Container>
  );
}
